import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    FormControlLabel,
    IconButton,
    MenuItem,
    Slider,
    Stack,
    Switch,
    Tab,
    Tabs,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    useTheme,
} from '@mui/material';
import { Add, Delete, DragIndicator, Map as MapIcon } from '@mui/icons-material';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    XAxis,
    YAxis,
} from 'recharts';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { format, subDays } from 'date-fns';
import { Button, DialogV2 } from 'c-components';
import { DeckHeatmapLayer } from 'c-components/DeckHeatmapLayer';
import type { Campaign } from 'c-sdk';
import { useCommonTranslation } from 'c-translation';
import { useLanguageSwitcher } from 'c-hooks';
import { ReportingMapOptions } from 'c-main/Lib';
import { LANG_en_gb } from 'c-lib';
import { useAtom } from 'jotai';
import ReportingConfigBar from 'c-main/Components/Campaign/ReportingConfigBar';
import {
    atom_campaignSummaryLayoutConfigs,
    atom_campaignSummaryWidgetConfigs,
    atom_campaignTopBoxConfigs,
    atom_summaryCustomizationDialogOpen,
} from 'c-main/Pages/Campaign/CampaignOverviewPage/atoms';
import type {
    SummaryLayoutConfig,
    SummaryWidgetConfig,
    SummaryWidgetDisplayMode,
    TopBoxConfig,
} from './summaryCustomizationConfig';
import {
    createEmptySummaryWidget,
    evenRowWidths,
    getDefaultSummaryLayout,
    getDefaultSummaryWidgets,
    getSummaryWidgetConfigKey,
    MIN_ITEM_WIDTH_PCT,
    MIN_ROW_HEIGHT_PX,
    MAX_ROW_HEIGHT_PX,
    DEFAULT_ROW_HEIGHT_PX,
    normalizeSummaryLayout,
    normalizeSummaryWidgetConfig,
    SUMMARY_WIDGET_GROUP_BY_OPTIONS,
    SUMMARY_WIDGET_GROUP_BY_TRANSLATION_KEYS,
    SUMMARY_WIDGET_KIND_OPTIONS,
    SUMMARY_WIDGET_MAP_DATA_TYPE_OPTIONS,
    SUMMARY_WIDGET_MAP_DISPLAY_OPTIONS,
    SUMMARY_WIDGET_METRIC_OPTIONS,
    DEFAULT_TOP_BOX_CONFIG,
    getTopBoxDefs,
} from './summaryCustomizationConfig';

export type SavedSummaryConfig = {
    widgets: SummaryWidgetConfig[];
    topBoxes: Record<string, TopBoxConfig>;
    layout: SummaryLayoutConfig;
};

type Props = {
    /** The campaign whose Summary is being customised. Not needed in managed mode. */
    campaign?: Campaign;
    // Render without the Dialog chrome, for embedding in a page (Admin -> Summary Setup).
    inline?: boolean;
    // Edit this saved summary rather than the campaign's session one. Supplying it with onSave
    // puts the editor in "managed" mode: nothing touches the per-campaign summary atoms, and
    // isDsp says which variant (top boxes, default widgets) the saved summary is for.
    value?: SavedSummaryConfig;
    isDsp?: boolean;
    onSave?: (config: SavedSummaryConfig) => void;
};

const compactFieldSx = {
    '& .MuiInputBase-root': {
        minHeight: 40,
    },
};

const PREVIEW_VALUES = [420, 680, 510, 940, 1180, 620, 820];

const PREVIEW_DOW_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const PREVIEW_HOUR_LABELS = Array.from({ length: 24 }, (_, i) => `${i}`);
// Plausible daily impact curve (low overnight, ramping to evening peak)
const PREVIEW_HOUR_VALUES = [
    160, 120, 100, 80, 60, 80, 140, 280, 480, 660, 780, 880, 960, 1020, 1080, 1140, 1200, 1240,
    1180, 1020, 820, 620, 420, 240,
];

const PREVIEW_GENERIC_LABELS = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];

const PREVIEW_MAP_CENTER = { lat: 51.509, lng: -0.118 };

const PREVIEW_MAP_MARKERS: { lat: number; lng: number }[] = [
    { lat: 51.5074, lng: -0.1278 },
    { lat: 51.5152, lng: -0.1418 },
    { lat: 51.5007, lng: -0.1246 },
    { lat: 51.5099, lng: -0.0867 },
    { lat: 51.5117, lng: -0.1109 },
    { lat: 51.4994, lng: -0.1746 },
    { lat: 51.5366, lng: -0.143 },
    { lat: 51.5045, lng: -0.0865 },
];

// Spread of weighted points so the deck.gl heatmap looks naturally dense.
const PREVIEW_HEATMAP_POINTS: { lat: number; lng: number; weight: number }[] = [
    { lat: 51.5074, lng: -0.1278, weight: 18 },
    { lat: 51.5081, lng: -0.1295, weight: 14 },
    { lat: 51.5095, lng: -0.131, weight: 16 },
    { lat: 51.512, lng: -0.122, weight: 12 },
    { lat: 51.5152, lng: -0.1418, weight: 10 },
    { lat: 51.5135, lng: -0.135, weight: 8 },
    { lat: 51.5007, lng: -0.1246, weight: 11 },
    { lat: 51.5028, lng: -0.126, weight: 9 },
    { lat: 51.5099, lng: -0.0867, weight: 13 },
    { lat: 51.5085, lng: -0.082, weight: 7 },
    { lat: 51.5117, lng: -0.1109, weight: 15 },
    { lat: 51.5128, lng: -0.115, weight: 10 },
    { lat: 51.4994, lng: -0.1746, weight: 6 },
    { lat: 51.501, lng: -0.179, weight: 4 },
    { lat: 51.5366, lng: -0.143, weight: 8 },
    { lat: 51.535, lng: -0.139, weight: 6 },
    { lat: 51.5045, lng: -0.0865, weight: 9 },
    { lat: 51.503, lng: -0.082, weight: 7 },
    { lat: 51.515, lng: -0.105, weight: 11 },
    { lat: 51.508, lng: -0.107, weight: 13 },
    { lat: 51.5165, lng: -0.0925, weight: 9 },
    { lat: 51.5195, lng: -0.115, weight: 7 },
    { lat: 51.4965, lng: -0.135, weight: 5 },
    { lat: 51.498, lng: -0.149, weight: 6 },
    { lat: 51.5205, lng: -0.158, weight: 5 },
    { lat: 51.526, lng: -0.155, weight: 7 },
];

type SummaryLayoutEditorProps = {
    layout: SummaryLayoutConfig;
    widgets: SummaryWidgetConfig[];
    getWidgetTitle: (widget: SummaryWidgetConfig) => string;
    onChange: (next: SummaryLayoutConfig) => void;
};

const SummaryLayoutEditor: React.FC<SummaryLayoutEditorProps> = ({
    layout,
    widgets,
    getWidgetTitle,
    onChange,
}) => {
    const rows = layout.rows;
    const titleFor = (widgetId: string) => {
        const widget = widgets.find(w => w.id === widgetId);
        return widget ? getWidgetTitle(widget) : widgetId;
    };

    const commitStructural = (nextRows: SummaryLayoutConfig['rows']) =>
        onChange(normalizeSummaryLayout({ mode: 'grid', rows: nextRows }, widgets));
    const commitWidths = (nextRows: SummaryLayoutConfig['rows']) =>
        onChange({ ...layout, mode: 'grid', rows: nextRows });

    const setMode = (mode: SummaryLayoutConfig['mode']) => onChange({ ...layout, mode });
    const setScroll = (scroll: boolean) => onChange({ ...layout, scroll });
    const addRow = () => commitStructural([...rows, { items: [] }]);
    const deleteRow = (rowIndex: number) => commitStructural(rows.filter((_, i) => i !== rowIndex));
    const setRowHeight = (rowIndex: number, heightPx: number) =>
        commitWidths(rows.map((row, i) => (i === rowIndex ? { ...row, heightPx } : row)));
    const distributeEven = (rowIndex: number) => {
        const evens = evenRowWidths(rows[rowIndex].items.length);
        commitStructural(
            rows.map((row, i) =>
                i === rowIndex
                    ? { items: row.items.map((it, j) => ({ ...it, widthPct: evens[j] })) }
                    : row,
            ),
        );
    };

    const moveItem = (widgetId: string, targetRowIndex: number, beforeWidgetId?: string) => {
        const stripped = rows.map(row => ({
            items: row.items.filter(it => it.widgetId !== widgetId),
        }));
        const targetItems = [...stripped[targetRowIndex].items];
        const beforeIdx = beforeWidgetId
            ? targetItems.findIndex(it => it.widgetId === beforeWidgetId)
            : -1;
        const insertAt = beforeIdx >= 0 ? beforeIdx : targetItems.length;
        targetItems.splice(insertAt, 0, { widgetId, widthPct: 0 });
        const evens = evenRowWidths(targetItems.length);
        stripped[targetRowIndex] = {
            items: targetItems.map((it, j) => ({ ...it, widthPct: evens[j] })),
        };
        commitStructural(stripped);
    };

    const dragRef = useRef<{
        rowIndex: number;
        leftIdx: number;
        startX: number;
        rowWidthPx: number;
        startLeft: number;
        startRight: number;
    } | null>(null);

    const onDividerPointerDown = (e: React.PointerEvent, rowIndex: number, leftIdx: number) => {
        const rowEl = (e.currentTarget as HTMLElement).closest('[data-layout-row]') as HTMLElement;
        if (!rowEl) return;
        const items = rows[rowIndex].items;
        dragRef.current = {
            rowIndex,
            leftIdx,
            startX: e.clientX,
            rowWidthPx: rowEl.getBoundingClientRect().width || 1,
            startLeft: items[leftIdx].widthPct,
            startRight: items[leftIdx + 1].widthPct,
        };
        window.addEventListener('pointermove', onDividerPointerMove);
        window.addEventListener('pointerup', onDividerPointerUp);
    };

    const onDividerPointerMove = (e: PointerEvent) => {
        const drag = dragRef.current;
        if (!drag) return;
        const deltaPct = ((e.clientX - drag.startX) / drag.rowWidthPx) * 100;
        const pairTotal = drag.startLeft + drag.startRight;
        const nextLeft = Math.max(
            MIN_ITEM_WIDTH_PCT,
            Math.min(pairTotal - MIN_ITEM_WIDTH_PCT, drag.startLeft + deltaPct),
        );
        const nextRight = pairTotal - nextLeft;
        commitWidths(
            rows.map((row, i) => {
                if (i !== drag.rowIndex) return row;
                return {
                    items: row.items.map((it, j) => {
                        if (j === drag.leftIdx) return { ...it, widthPct: nextLeft };
                        if (j === drag.leftIdx + 1) return { ...it, widthPct: nextRight };
                        return it;
                    }),
                };
            }),
        );
    };

    const onDividerPointerUp = () => {
        dragRef.current = null;
        window.removeEventListener('pointermove', onDividerPointerMove);
        window.removeEventListener('pointerup', onDividerPointerUp);
    };

    const isGrid = layout.mode === 'grid';

    return (
        <Stack gap={1.5} flex={1}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                <Typography variant="body2" color="text.secondary">
                    Choose how the Key Breakdowns widgets are arranged.
                </Typography>
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={layout.mode}
                    onChange={(_, val) => val && setMode(val)}
                >
                    <ToggleButton value="carousel">Scroll</ToggleButton>
                    <ToggleButton value="grid">Custom grid</ToggleButton>
                </ToggleButtonGroup>
            </Stack>

            {!isGrid && (
                <Box
                    sx={{
                        border: th => `1px dashed ${th.palette.divider}`,
                        borderRadius: 1,
                        p: 3,
                        textAlign: 'center',
                        color: 'text.secondary',
                    }}
                >
                    <Typography variant="body2">
                        Widgets scroll in a carousel. Switch to “Custom grid” to arrange them into
                        rows and set widths.
                    </Typography>
                </Box>
            )}

            {isGrid && (
                <>
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={1}
                        flexWrap="wrap"
                    >
                        <Button
                            variant="outlined"
                            onClick={addRow}
                            startIcon={<Add fontSize="small" />}
                            sx={{ alignSelf: 'flex-start' }}
                        >
                            Add row
                        </Button>
                        <FormControlLabel
                            control={
                                <Switch
                                    size="small"
                                    checked={layout.scroll === true}
                                    onChange={(_, checked) => setScroll(checked)}
                                />
                            }
                            label="Page extra widgets with arrows (>4 per row)"
                        />
                    </Stack>
                    <Stack gap={1.25}>
                        {rows.map((row, rowIndex) => (
                            <Box
                                // eslint-disable-next-line react/no-array-index-key
                                key={rowIndex}
                                sx={{
                                    border: th => `1px solid ${th.palette.divider}`,
                                    borderRadius: 1,
                                    p: 1,
                                }}
                            >
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    sx={{ mb: 0.75 }}
                                >
                                    <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                        Row {rowIndex + 1}
                                    </Typography>
                                    <Stack direction="row" gap={0.5}>
                                        <Button
                                            size="small"
                                            variant="text"
                                            disabled={row.items.length < 2}
                                            onClick={() => distributeEven(rowIndex)}
                                        >
                                            Even widths
                                        </Button>
                                        <IconButton
                                            size="small"
                                            onClick={() => deleteRow(rowIndex)}
                                            sx={{ p: 0.5 }}
                                        >
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    </Stack>
                                </Stack>
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    gap={1}
                                    sx={{ mb: 0.75, px: 0.5 }}
                                >
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ minWidth: 42 }}
                                    >
                                        Height
                                    </Typography>
                                    <Slider
                                        size="small"
                                        min={MIN_ROW_HEIGHT_PX}
                                        max={MAX_ROW_HEIGHT_PX}
                                        step={10}
                                        value={row.heightPx ?? DEFAULT_ROW_HEIGHT_PX}
                                        onChange={(_, val) =>
                                            setRowHeight(
                                                rowIndex,
                                                Array.isArray(val) ? val[0] : val,
                                            )
                                        }
                                        sx={{ flex: 1 }}
                                    />
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ minWidth: 42, textAlign: 'right' }}
                                    >
                                        {row.heightPx ?? DEFAULT_ROW_HEIGHT_PX}px
                                    </Typography>
                                </Stack>
                                <Box
                                    data-layout-row
                                    onDragOver={e => e.preventDefault()}
                                    onDrop={e => {
                                        e.preventDefault();
                                        const id = e.dataTransfer.getData('text/plain');
                                        if (id) moveItem(id, rowIndex);
                                    }}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'stretch',
                                        minHeight: 52,
                                        gap: 0,
                                        bgcolor: 'action.hover',
                                        borderRadius: 1,
                                        p: 0.5,
                                        ...(layout.scroll
                                            ? { flexWrap: 'nowrap', overflowX: 'auto' }
                                            : { flexWrap: 'wrap' }),
                                    }}
                                >
                                    {row.items.length === 0 && (
                                        <Box
                                            sx={{
                                                flex: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                color: 'text.disabled',
                                                fontSize: 12,
                                            }}
                                        >
                                            Drop widgets here
                                        </Box>
                                    )}
                                    {row.items.map((item, itemIndex) => (
                                        <React.Fragment key={item.widgetId}>
                                            <Box
                                                draggable
                                                onDragStart={e => {
                                                    e.dataTransfer.setData(
                                                        'text/plain',
                                                        item.widgetId,
                                                    );
                                                    e.dataTransfer.effectAllowed = 'move';
                                                }}
                                                onDragOver={e => e.preventDefault()}
                                                onDrop={e => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    const id = e.dataTransfer.getData('text/plain');
                                                    if (id && id !== item.widgetId) {
                                                        moveItem(id, rowIndex, item.widgetId);
                                                    }
                                                }}
                                                sx={{
                                                    flexBasis: `${item.widthPct}%`,
                                                    flexGrow: 0,
                                                    flexShrink: 0,
                                                    minWidth: 0,
                                                    bgcolor: 'background.paper',
                                                    border: th => `1px solid ${th.palette.divider}`,
                                                    borderRadius: 1,
                                                    px: 1,
                                                    py: 0.75,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.5,
                                                    cursor: 'grab',
                                                    minHeight: 44,
                                                }}
                                            >
                                                <DragIndicator
                                                    fontSize="small"
                                                    color="action"
                                                    sx={{ flexShrink: 0 }}
                                                />
                                                <Typography
                                                    variant="caption"
                                                    noWrap
                                                    sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}
                                                    title={titleFor(item.widgetId)}
                                                >
                                                    {titleFor(item.widgetId)}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ flexShrink: 0 }}
                                                >
                                                    {Math.round(item.widthPct)}%
                                                </Typography>
                                            </Box>
                                            {itemIndex < row.items.length - 1 && (
                                                <Box
                                                    onPointerDown={e =>
                                                        onDividerPointerDown(e, rowIndex, itemIndex)
                                                    }
                                                    sx={{
                                                        width: 10,
                                                        flexShrink: 0,
                                                        cursor: 'col-resize',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        '&:hover .divider-line': {
                                                            bgcolor: 'primary.main',
                                                        },
                                                    }}
                                                >
                                                    <Box
                                                        className="divider-line"
                                                        sx={{
                                                            width: 3,
                                                            height: '60%',
                                                            borderRadius: 2,
                                                            bgcolor: 'divider',
                                                        }}
                                                    />
                                                </Box>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </Box>
                            </Box>
                        ))}
                    </Stack>
                </>
            )}
        </Stack>
    );
};

const SummaryCustomizationDialog: React.FC<Props> = ({
    campaign,
    inline = false,
    value,
    isDsp: isDspProp,
    onSave,
}) => {
    const t = useCommonTranslation();
    const theme = useTheme();
    const { lang } = useLanguageSwitcher();
    const isUKLocale = lang === LANG_en_gb;
    const dateFormatString = isUKLocale ? 'd MMM yyyy' : 'MMM d, yyyy';
    const managed = onSave != null;
    const [dialogOpen, setOpen] = useAtom(atom_summaryCustomizationDialogOpen);
    const open = inline || dialogOpen;
    const [summaryConfigs, setSummaryConfigs] = useAtom(atom_campaignSummaryWidgetConfigs);
    const [topBoxConfigs, setTopBoxConfigs] = useAtom(atom_campaignTopBoxConfigs);
    const [layoutConfigs, setLayoutConfigs] = useAtom(atom_campaignSummaryLayoutConfigs);
    const [activeTab, setActiveTab] = useState<'widgets' | 'layout'>('widgets');

    const isDsp = useMemo(
        () =>
            managed
                ? isDspProp === true
                : campaign != null && campaign.type !== 'linear' && campaign.type !== 'default',
        [managed, isDspProp, campaign],
    );

    const configKey = useMemo(
        () => getSummaryWidgetConfigKey(campaign?.id ?? 0, isDsp),
        [campaign?.id, isDsp],
    );
    const storedWidgets = managed ? value?.widgets : summaryConfigs[configKey];
    const storedTopBoxes = managed ? value?.topBoxes : topBoxConfigs[configKey];
    const storedLayout = managed ? value?.layout : layoutConfigs[configKey];
    const [draftWidgets, setDraftWidgets] = useState<SummaryWidgetConfig[]>([]);
    const [draftTopBoxes, setDraftTopBoxes] = useState<Record<string, TopBoxConfig>>({});
    const [draftLayout, setDraftLayout] = useState<SummaryLayoutConfig>(getDefaultSummaryLayout());
    const [draggedWidgetId, setDraggedWidgetId] = useState<string | null>(null);
    const [selectedWidgetId, setSelectedWidgetId] = useState<string | null>(null);

    useEffect(() => {
        if (!open) return;
        const normalized = (storedWidgets ?? getDefaultSummaryWidgets(isDsp)).map(
            normalizeSummaryWidgetConfig,
        );
        setDraftWidgets(normalized);
        setSelectedWidgetId(normalized[0]?.id ?? null);
        setDraftTopBoxes(storedTopBoxes ?? {});
        setDraftLayout(normalizeSummaryLayout(storedLayout, normalized));
    }, [isDsp, open, storedWidgets, storedTopBoxes, storedLayout]);

    useEffect(() => {
        if (!open) return;
        setDraftLayout(current => normalizeSummaryLayout(current, draftWidgets));
    }, [open, draftWidgets]);

    const updateTopBox = (key: string, patch: Partial<TopBoxConfig>) => {
        setDraftTopBoxes(current => ({
            ...current,
            [key]: { ...DEFAULT_TOP_BOX_CONFIG, ...current[key], ...patch },
        }));
    };

    const selectedWidget = useMemo(
        () =>
            draftWidgets.find(widget => widget.id === selectedWidgetId) ?? draftWidgets[0] ?? null,
        [draftWidgets, selectedWidgetId],
    );

    const updateSelectedWidget = (next: SummaryWidgetConfig) => {
        if (!selectedWidget) return;
        setDraftWidgets(current =>
            current.map(widget =>
                widget.id === selectedWidget.id ? normalizeSummaryWidgetConfig(next) : widget,
            ),
        );
    };

    const setPrimary = (id: string, makePrimary: boolean) => {
        setDraftWidgets(current =>
            current.map(widget => {
                if (widget.id === id) return { ...widget, isPrimary: makePrimary };
                if (makePrimary && widget.isPrimary) {
                    const { isPrimary: _drop, ...rest } = widget;
                    return rest;
                }
                return widget;
            }),
        );
    };

    const removeWidget = (id: string) => {
        setDraftWidgets(current => {
            const removedIndex = current.findIndex(widget => widget.id === id);
            const next = current.filter(widget => widget.id !== id);
            if (selectedWidgetId === id) {
                const nextSelected =
                    next[removedIndex] ?? next[removedIndex - 1] ?? next[0] ?? null;
                setSelectedWidgetId(nextSelected?.id ?? null);
            }
            return next;
        });
    };

    const addWidget = () => {
        const created = normalizeSummaryWidgetConfig(createEmptySummaryWidget());
        setDraftWidgets(current => [...current, created]);
        setSelectedWidgetId(created.id);
    };

    const moveWidget = (draggedId: string, targetId: string) => {
        if (draggedId === targetId) return;

        setDraftWidgets(current => {
            const draggedIndex = current.findIndex(widget => widget.id === draggedId);
            const targetIndex = current.findIndex(widget => widget.id === targetId);

            if (draggedIndex === -1 || targetIndex === -1) return current;

            const next = [...current];
            const [draggedWidget] = next.splice(draggedIndex, 1);
            next.splice(targetIndex, 0, draggedWidget);
            return next;
        });
    };

    const applyChanges = () => {
        const nextWidgets = draftWidgets.map(normalizeSummaryWidgetConfig);
        if (managed) {
            onSave({
                widgets: nextWidgets,
                topBoxes: draftTopBoxes,
                layout: normalizeSummaryLayout(draftLayout, nextWidgets),
            });
            return;
        }
        setSummaryConfigs(current => ({
            ...current,
            [configKey]: nextWidgets,
        }));
        setTopBoxConfigs(current => ({
            ...current,
            [configKey]: draftTopBoxes,
        }));
        setLayoutConfigs(current => ({
            ...current,
            [configKey]: normalizeSummaryLayout(draftLayout, nextWidgets),
        }));
        setOpen(false);
    };

    const resetChanges = () => {
        const normalized = getDefaultSummaryWidgets(isDsp).map(normalizeSummaryWidgetConfig);
        setDraftWidgets(normalized);
        setSelectedWidgetId(normalized[0]?.id ?? null);
        setDraftTopBoxes({});
        setDraftLayout(normalizeSummaryLayout(getDefaultSummaryLayout(), normalized));
    };

    const getMapDataTypeLabelPath = (widget: SummaryWidgetConfig) => (mapDataType: number) => {
        switch (mapDataType) {
            case 1:
                return `Modules.Main.Campaigns.Overview.Reports.${widget.metric}.mapSettingActualHeat`;
            case 2:
                return `Modules.Main.Campaigns.Overview.Reports.${widget.metric}.mapSettingExpectedHeat`;
            case 3:
                return 'Modules.Main.Campaigns.Overview.Reports.map.heatmapPerformance';
            default:
                return `Modules.Main.Campaigns.Overview.Reports.${widget.metric}.mapSettingActualHeat`;
        }
    };

    const getKindLabel = (kind: SummaryWidgetConfig['kind']) => {
        const option = SUMMARY_WIDGET_KIND_OPTIONS.find(o => o.value === kind);
        return option ? t(option.labelPath as any) : kind;
    };

    const getMetricLabel = (metric: SummaryWidgetConfig['metric']) => {
        const option = SUMMARY_WIDGET_METRIC_OPTIONS.find(o => o.value === metric);
        return option ? t(option.labelPath as any) : metric;
    };

    const getGroupByLabel = (groupBy: string) => {
        if (groupBy === 'date') return 'Date';
        const key =
            SUMMARY_WIDGET_GROUP_BY_TRANSLATION_KEYS[groupBy] ??
            `Modules.Main.Campaigns.Overview.Reports.fields.${groupBy}`;
        return t(key as any);
    };

    const getWidgetTitle = (widget: SummaryWidgetConfig): string => {
        const base = `${getMetricLabel(widget.metric)} ${getKindLabel(widget.kind)}`;
        if (widget.kind === 'map') {
            const mapTypeOption = SUMMARY_WIDGET_MAP_DATA_TYPE_OPTIONS.find(
                o => o.value === widget.mapDataType,
            );
            if (!mapTypeOption) return base;
            return `${base} · ${t(getMapDataTypeLabelPath(widget)(mapTypeOption.value) as any)}`;
        }
        const groupByLabels = widget.groupBy.filter(Boolean).map(getGroupByLabel);
        if (groupByLabels.length === 0) return base;
        return `${base} · ${groupByLabels.join(' · ')}`;
    };

    const getWidgetSubtitle = (widget: SummaryWidgetConfig): string => {
        if (widget.kind === 'map') {
            const mapTypeOption = SUMMARY_WIDGET_MAP_DATA_TYPE_OPTIONS.find(
                o => o.value === widget.mapDataType,
            );
            const displayOption = SUMMARY_WIDGET_MAP_DISPLAY_OPTIONS.find(
                o => o.value === widget.mapDisplayMode,
            );
            const parts: string[] = [];
            if (mapTypeOption) {
                parts.push(t(getMapDataTypeLabelPath(widget)(mapTypeOption.value) as any));
            }
            if (displayOption) {
                parts.push(t(displayOption.labelPath as any));
            }
            return parts.join(' · ');
        }
        return widget.groupBy.filter(Boolean).map(getGroupByLabel).join(' · ');
    };

    const getPreviewData = (widget: SummaryWidgetConfig) => {
        const groupBy = widget.groupBy[0];
        if (groupBy === 'date') {
            const today = new Date();
            // Date series is a running total — monotonically increasing
            let runningTotal = 0;
            return Array.from({ length: 7 }, (_, i) => {
                runningTotal += PREVIEW_VALUES[i] ?? 0;
                return {
                    name: format(subDays(today, 6 - i), dateFormatString),
                    value: runningTotal,
                };
            });
        }
        if (groupBy === 'day_of_week') {
            return PREVIEW_DOW_LABELS.map((name, i) => ({ name, value: PREVIEW_VALUES[i] ?? 0 }));
        }
        if (groupBy === 'hour') {
            return PREVIEW_HOUR_LABELS.map((name, i) => ({
                name,
                value: PREVIEW_HOUR_VALUES[i] ?? 0,
            }));
        }
        return PREVIEW_GENERIC_LABELS.map((name, i) => ({ name, value: PREVIEW_VALUES[i] ?? 0 }));
    };

    const heatmapPreviewData = useMemo(() => {
        if (typeof window === 'undefined' || !(window as any).google?.maps) return [];
        return PREVIEW_HEATMAP_POINTS.map(p => ({
            location: new google.maps.LatLng(p.lat, p.lng),
            weight: p.weight,
        }));
    }, []);

    const renderWidgetPreview = (widget: SummaryWidgetConfig) => {
        if (widget.kind === 'map') {
            const hasGoogleMaps =
                typeof window !== 'undefined' && (window as any).google?.maps != null;
            const showHeatmap = widget.mapDisplayMode !== 'screens';
            const showMarkers = widget.mapDisplayMode !== 'heatmap';

            if (!hasGoogleMaps) {
                return (
                    <Stack
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                            height: '100%',
                            width: '100%',
                            borderRadius: 1,
                            bgcolor: '#eef3ef',
                        }}
                        gap={1}
                    >
                        <MapIcon sx={{ fontSize: 40, color: 'rgba(0, 0, 0, 0.45)' }} />
                        <Typography variant="caption" sx={{ color: 'rgba(0, 0, 0, 0.55)' }}>
                            {getWidgetSubtitle(widget) || getKindLabel(widget.kind)}
                        </Typography>
                    </Stack>
                );
            }

            return (
                <Box
                    sx={{
                        position: 'relative',
                        width: '100%',
                        height: '100%',
                        borderRadius: 1,
                        overflow: 'hidden',
                    }}
                >
                    <GoogleMap
                        mapContainerStyle={{ width: '100%', height: '100%' }}
                        center={PREVIEW_MAP_CENTER}
                        zoom={11}
                        options={{
                            ...ReportingMapOptions,
                            disableDefaultUI: true,
                            gestureHandling: 'none',
                            keyboardShortcuts: false,
                            clickableIcons: false,
                        }}
                    >
                        {showHeatmap && <DeckHeatmapLayer data={heatmapPreviewData} />}
                        {showMarkers &&
                            PREVIEW_MAP_MARKERS.map((position, i) => (
                                <Marker
                                    /* eslint-disable-next-line react/no-array-index-key */
                                    key={`preview-marker-${i}`}
                                    position={position}
                                />
                            ))}
                    </GoogleMap>
                </Box>
            );
        }

        const data = getPreviewData(widget);

        if (widget.kind === 'line') {
            return (
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} width={36} />
                        <Line
                            type="monotone"
                            dataKey="value"
                            stroke={theme.palette.primary.main}
                            strokeWidth={2}
                            dot={false}
                            isAnimationActive={false}
                        />
                    </LineChart>
                </ResponsiveContainer>
            );
        }

        return (
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} width={36} />
                    <Bar
                        dataKey="value"
                        fill={theme.palette.primary.main}
                        radius={[2, 2, 0, 0]}
                        isAnimationActive={false}
                    />
                </BarChart>
            </ResponsiveContainer>
        );
    };

    const renderWidgetEditor = (widget: SummaryWidgetConfig) => (
        <Stack gap={1.25}>
            <Stack direction={{ xs: 'column', md: 'row' }} gap={1}>
                <TextField
                    select
                    fullWidth
                    size="small"
                    label={t('Modules.Main.Campaigns.Overview.SummaryCustomization.metric')}
                    value={widget.metric}
                    onChange={e =>
                        updateSelectedWidget({
                            ...widget,
                            metric: e.target.value,
                        })
                    }
                    sx={compactFieldSx}
                >
                    {SUMMARY_WIDGET_METRIC_OPTIONS.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                            {t(option.labelPath as any)}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    select
                    fullWidth
                    size="small"
                    label={t('Modules.Main.Campaigns.Overview.SummaryCustomization.chartType')}
                    value={widget.kind}
                    onChange={e =>
                        updateSelectedWidget({
                            ...widget,
                            kind: e.target.value as SummaryWidgetConfig['kind'],
                            groupBy:
                                e.target.value === 'map'
                                    ? []
                                    : widget.groupBy.length > 0
                                    ? widget.groupBy
                                    : ['display_owner'],
                        })
                    }
                    sx={compactFieldSx}
                >
                    {SUMMARY_WIDGET_KIND_OPTIONS.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                            {t(option.labelPath as any)}
                        </MenuItem>
                    ))}
                </TextField>
            </Stack>

            {widget.kind === 'map' ? (
                <Stack direction={{ xs: 'column', md: 'row' }} gap={1}>
                    <TextField
                        select
                        fullWidth
                        size="small"
                        label={t('Modules.Main.Campaigns.Overview.SummaryCustomization.mapView')}
                        value={widget.mapDataType ?? ''}
                        onChange={e =>
                            updateSelectedWidget({
                                ...widget,
                                mapDataType: Number(e.target.value) as any,
                            })
                        }
                        sx={compactFieldSx}
                    >
                        {SUMMARY_WIDGET_MAP_DATA_TYPE_OPTIONS.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                                {t(getMapDataTypeLabelPath(widget)(option.value) as any)}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select
                        fullWidth
                        size="small"
                        label={t('Modules.Main.Campaigns.Overview.SummaryCustomization.mapDisplay')}
                        value={widget.mapDisplayMode ?? 'heatmap'}
                        onChange={e =>
                            updateSelectedWidget({
                                ...widget,
                                mapDisplayMode: e.target.value as any,
                            })
                        }
                        sx={compactFieldSx}
                    >
                        {SUMMARY_WIDGET_MAP_DISPLAY_OPTIONS.map(option => (
                            <MenuItem key={option.value} value={option.value}>
                                {t(option.labelPath as any)}
                            </MenuItem>
                        ))}
                    </TextField>
                </Stack>
            ) : (
                <Stack direction={{ xs: 'column', md: 'row' }} gap={1}>
                    <TextField
                        select
                        fullWidth
                        size="small"
                        label={t(
                            'Modules.Main.Campaigns.Overview.SummaryCustomization.primaryGroupBy',
                        )}
                        value={widget.groupBy[0] ?? ''}
                        onChange={e =>
                            updateSelectedWidget({
                                ...widget,
                                groupBy: [e.target.value, widget.groupBy[1]].filter(Boolean),
                            })
                        }
                        sx={compactFieldSx}
                    >
                        {SUMMARY_WIDGET_GROUP_BY_OPTIONS.map(option => (
                            <MenuItem key={option} value={option}>
                                {getGroupByLabel(option)}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select
                        fullWidth
                        size="small"
                        label={t(
                            'Modules.Main.Campaigns.Overview.SummaryCustomization.secondaryGroupBy',
                        )}
                        value={widget.groupBy[1] ?? ''}
                        onChange={e =>
                            updateSelectedWidget({
                                ...widget,
                                groupBy: [widget.groupBy[0], e.target.value].filter(Boolean),
                            })
                        }
                        sx={compactFieldSx}
                    >
                        <MenuItem value="">
                            {t('Modules.Main.Campaigns.Overview.SummaryCustomization.none')}
                        </MenuItem>
                        {SUMMARY_WIDGET_GROUP_BY_OPTIONS.map(option => (
                            <MenuItem key={option} value={option}>
                                {getGroupByLabel(option)}
                            </MenuItem>
                        ))}
                    </TextField>
                </Stack>
            )}
            <FormControlLabel
                control={
                    <Switch
                        size="small"
                        checked={widget.isPrimary === true}
                        onChange={(_, checked) => setPrimary(widget.id, checked)}
                    />
                }
                label="Show as primary (largest) widget"
            />
            {widget.kind === 'bar' && (
                <Stack direction={{ xs: 'column', md: 'row' }} gap={1}>
                    <TextField
                        select
                        fullWidth
                        size="small"
                        label="Small view style"
                        value={widget.displayMode ?? 'chart'}
                        onChange={e =>
                            updateSelectedWidget({
                                ...widget,
                                displayMode: e.target.value as SummaryWidgetDisplayMode,
                            })
                        }
                        sx={compactFieldSx}
                    >
                        <MenuItem value="chart">Bar chart</MenuItem>
                        <MenuItem value="leaderboard">Leaderboard (top 7)</MenuItem>
                        {widget.groupBy.length === 1 && (
                            <MenuItem value="overlayBars">Overlay bars</MenuItem>
                        )}
                    </TextField>
                    <TextField
                        select
                        fullWidth
                        size="small"
                        label="Expanded view style"
                        value={widget.expandedDisplayMode ?? 'chart'}
                        onChange={e =>
                            updateSelectedWidget({
                                ...widget,
                                expandedDisplayMode: e.target.value as SummaryWidgetDisplayMode,
                            })
                        }
                        sx={compactFieldSx}
                    >
                        <MenuItem value="chart">Bar chart</MenuItem>
                        <MenuItem value="leaderboard">Leaderboard (all)</MenuItem>
                        {widget.groupBy.length === 1 && (
                            <MenuItem value="overlayBars">Overlay bars</MenuItem>
                        )}
                    </TextField>
                </Stack>
            )}
            {widget.kind === 'bar' && (widget.displayMode ?? 'chart') === 'chart' && (
                <FormControlLabel
                    control={
                        <Switch
                            size="small"
                            checked={widget.barOrientation === 'horizontal'}
                            onChange={(_, checked) =>
                                updateSelectedWidget({
                                    ...widget,
                                    barOrientation: checked ? 'horizontal' : 'vertical',
                                })
                            }
                        />
                    }
                    label="Horizontal bars (small view)"
                />
            )}
            {widget.kind === 'bar' && (widget.expandedDisplayMode ?? 'chart') === 'chart' && (
                <FormControlLabel
                    control={
                        <Switch
                            size="small"
                            checked={widget.expandedBarOrientation === 'horizontal'}
                            onChange={(_, checked) =>
                                updateSelectedWidget({
                                    ...widget,
                                    expandedBarOrientation: checked ? 'horizontal' : 'vertical',
                                })
                            }
                        />
                    }
                    label="Horizontal bars (expanded view)"
                />
            )}
            {widget.kind === 'line' && (
                <FormControlLabel
                    control={
                        <Switch
                            size="small"
                            checked={widget.cumulative !== false}
                            onChange={(_, checked) =>
                                updateSelectedWidget({
                                    ...widget,
                                    cumulative: checked,
                                })
                            }
                        />
                    }
                    label="Accumulate (running total)"
                />
            )}
            {widget.kind !== 'map' && (
                <>
                    <Typography
                        variant="caption"
                        sx={{ fontWeight: 700, color: 'text.secondary', mt: 0.5 }}
                    >
                        Default controls
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} flexWrap="wrap">
                        <FormControlLabel
                            control={
                                <Switch
                                    size="small"
                                    checked={widget.defaultShowNumbers === true}
                                    onChange={(_, checked) =>
                                        updateSelectedWidget({
                                            ...widget,
                                            defaultShowNumbers: checked,
                                        })
                                    }
                                />
                            }
                            label="Show numbers"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    size="small"
                                    checked={widget.defaultShowPercent === true}
                                    onChange={(_, checked) =>
                                        updateSelectedWidget({
                                            ...widget,
                                            defaultShowPercent: checked,
                                        })
                                    }
                                />
                            }
                            label="Show percentage"
                        />
                    </Stack>
                    <Stack direction={{ xs: 'column', md: 'row' }} gap={1}>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Default view"
                            value={widget.defaultViewFilter ?? 'Both'}
                            onChange={e =>
                                updateSelectedWidget({
                                    ...widget,
                                    defaultViewFilter: e.target
                                        .value as SummaryWidgetConfig['defaultViewFilter'],
                                })
                            }
                            sx={compactFieldSx}
                        >
                            <MenuItem value="Both">Both series</MenuItem>
                            <MenuItem value="Delivered">Delivered only</MenuItem>
                            <MenuItem value="Expected">Expected only</MenuItem>
                        </TextField>
                        {widget.kind === 'bar' && (
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="Default sort"
                                value={widget.defaultSort ?? 'desc'}
                                onChange={e =>
                                    updateSelectedWidget({
                                        ...widget,
                                        defaultSort: e.target
                                            .value as SummaryWidgetConfig['defaultSort'],
                                    })
                                }
                                sx={compactFieldSx}
                            >
                                <MenuItem value="desc">Highest first</MenuItem>
                                <MenuItem value="asc">Lowest first</MenuItem>
                                <MenuItem value="alpha">Alphabetical</MenuItem>
                                <MenuItem value="none">Original order</MenuItem>
                            </TextField>
                        )}
                        {widget.kind === 'bar' && (
                            <TextField
                                fullWidth
                                size="small"
                                type="number"
                                label="Items to show (small view)"
                                value={widget.smallItemLimit ?? ''}
                                placeholder="7"
                                inputProps={{ min: 1, step: 1 }}
                                onChange={e => {
                                    const raw = e.target.value;
                                    const n = Math.floor(Number(raw));
                                    updateSelectedWidget({
                                        ...widget,
                                        smallItemLimit:
                                            raw === '' || !Number.isFinite(n)
                                                ? undefined
                                                : Math.max(1, n),
                                    });
                                }}
                                sx={compactFieldSx}
                            />
                        )}
                    </Stack>
                </>
            )}
        </Stack>
    );

    const actions = (
        <Stack direction="row" gap={1}>
            <Button onClick={resetChanges}>
                {t('Modules.Main.Campaigns.Overview.SummaryCustomization.reset')}
            </Button>
            <Button variant="contained" onClick={applyChanges}>
                {managed ? 'Save' : t('Modules.Main.Campaigns.Overview.ActionLabels.apply')}
            </Button>
        </Stack>
    );

    const body = (
        <Stack gap={2} flex={1}>
            {/* A saved summary is renamed and saved from its own page, so the save/load-by-name
                bar is only for customising a campaign's summary. */}
            {!managed && (
                <ReportingConfigBar
                    type="summary"
                    campaignType={isDsp ? 'dsp' : 'linear'}
                    getConfig={() =>
                        JSON.stringify({
                            widgets: draftWidgets,
                            topBoxes: draftTopBoxes,
                            layout: draftLayout,
                        })
                    }
                    onLoad={config => {
                        try {
                            const parsed = JSON.parse(config) ?? {};
                            const widgets = Array.isArray(parsed.widgets)
                                ? parsed.widgets.map(normalizeSummaryWidgetConfig)
                                : [];
                            setDraftWidgets(widgets);
                            setSelectedWidgetId(widgets[0]?.id ?? null);
                            setDraftTopBoxes(parsed.topBoxes ?? {});
                            setDraftLayout(normalizeSummaryLayout(parsed.layout, widgets));
                        } catch {
                            setDraftWidgets(current => current);
                        }
                    }}
                />
            )}
            <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                sx={{ minHeight: 40, borderBottom: th => `1px solid ${th.palette.divider}` }}
            >
                <Tab value="widgets" label="Widgets" sx={{ minHeight: 40 }} />
                <Tab value="layout" label="Layout" sx={{ minHeight: 40 }} />
            </Tabs>
            {activeTab === 'layout' && (
                <SummaryLayoutEditor
                    layout={draftLayout}
                    widgets={draftWidgets}
                    getWidgetTitle={getWidgetTitle}
                    onChange={setDraftLayout}
                />
            )}
            {activeTab === 'widgets' && (
                <>
                    <Box
                        sx={{
                            border: th => `1px solid ${th.palette.divider}`,
                            borderRadius: 1,
                            p: 1.5,
                        }}
                    >
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                            Top summary boxes
                        </Typography>
                        <Stack gap={0.25}>
                            {getTopBoxDefs(isDsp).map(box => {
                                const cfg = {
                                    ...DEFAULT_TOP_BOX_CONFIG,
                                    ...draftTopBoxes[box.key],
                                };
                                return (
                                    <Stack
                                        key={box.key}
                                        direction={{ xs: 'column', sm: 'row' }}
                                        alignItems={{ sm: 'center' }}
                                        justifyContent="space-between"
                                        gap={0.5}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 600, minWidth: 160 }}
                                        >
                                            {box.label}
                                        </Typography>
                                        <Stack direction="row" gap={2}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        size="small"
                                                        checked={!cfg.hidden}
                                                        onChange={(_, checked) =>
                                                            updateTopBox(box.key, {
                                                                hidden: !checked,
                                                            })
                                                        }
                                                    />
                                                }
                                                label="Show box"
                                            />
                                            <FormControlLabel
                                                disabled={cfg.hidden}
                                                control={
                                                    <Switch
                                                        size="small"
                                                        checked={cfg.showGraph}
                                                        onChange={(_, checked) =>
                                                            updateTopBox(box.key, {
                                                                showGraph: checked,
                                                            })
                                                        }
                                                    />
                                                }
                                                label="Show graph"
                                            />
                                            <FormControlLabel
                                                disabled={cfg.hidden}
                                                control={
                                                    <Switch
                                                        size="small"
                                                        checked={cfg.showAllData}
                                                        onChange={(_, checked) =>
                                                            updateTopBox(box.key, {
                                                                showAllData: checked,
                                                            })
                                                        }
                                                    />
                                                }
                                                label="Show all data"
                                            />
                                            <FormControlLabel
                                                disabled={cfg.hidden || !cfg.showGraph}
                                                control={
                                                    <Switch
                                                        size="small"
                                                        checked={cfg.cumulative}
                                                        onChange={(_, checked) =>
                                                            updateTopBox(box.key, {
                                                                cumulative: checked,
                                                            })
                                                        }
                                                    />
                                                }
                                                label="Accumulate"
                                            />
                                        </Stack>
                                    </Stack>
                                );
                            })}
                        </Stack>
                    </Box>
                    <Stack direction={{ xs: 'column', lg: 'row' }} gap={2} flex={1}>
                        <Stack gap={1.25} sx={{ width: { xs: '100%', lg: 310 }, flexShrink: 0 }}>
                            <Typography variant="h5">
                                {t('Modules.Main.Campaigns.Overview.ActionLabels.customizeSummary')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t(
                                    'Modules.Main.Campaigns.Overview.SummaryCustomization.description',
                                )}
                            </Typography>

                            <Button
                                variant="outlined"
                                onClick={addWidget}
                                startIcon={<Add fontSize="small" />}
                                sx={{ alignSelf: 'flex-start' }}
                            >
                                {t(
                                    'Modules.Main.Campaigns.Overview.SummaryCustomization.addWidget',
                                )}
                            </Button>

                            <Stack gap={0.75} sx={{ maxHeight: 480, overflowY: 'auto', pr: 0.5 }}>
                                {draftWidgets.map((widget, index) => {
                                    const isActive = widget.id === selectedWidget?.id;
                                    const isBeingDragged = widget.id === draggedWidgetId;
                                    return (
                                        <Box
                                            key={widget.id}
                                            draggable
                                            onDragStart={e => {
                                                setDraggedWidgetId(widget.id);
                                                e.dataTransfer.setData('text/plain', widget.id);
                                                e.dataTransfer.effectAllowed = 'move';
                                            }}
                                            onDragEnd={() => setDraggedWidgetId(null)}
                                            onDragOver={e => {
                                                e.preventDefault();
                                                e.dataTransfer.dropEffect = 'move';
                                            }}
                                            onDrop={e => {
                                                e.preventDefault();
                                                const sourceId =
                                                    e.dataTransfer.getData('text/plain') ||
                                                    draggedWidgetId;
                                                if (!sourceId) return;
                                                moveWidget(sourceId, widget.id);
                                                setDraggedWidgetId(null);
                                            }}
                                            onClick={() => setSelectedWidgetId(widget.id)}
                                            sx={{
                                                border: '1px solid',
                                                borderColor: isActive ? 'primary.main' : 'divider',
                                                borderRadius: 1.5,
                                                p: 1.25,
                                                cursor: 'pointer',
                                                bgcolor: isActive
                                                    ? 'action.selected'
                                                    : 'background.paper',
                                                opacity: isBeingDragged ? 0.6 : 1,
                                                transition:
                                                    'border-color 0.2s ease, background-color 0.2s ease',
                                            }}
                                        >
                                            <Stack
                                                direction="row"
                                                alignItems="center"
                                                justifyContent="space-between"
                                                gap={1}
                                            >
                                                <Stack
                                                    direction="row"
                                                    alignItems="center"
                                                    gap={0.5}
                                                    minWidth={0}
                                                >
                                                    <DragIndicator
                                                        fontSize="small"
                                                        color="action"
                                                        sx={{ cursor: 'grab' }}
                                                    />
                                                    <Typography variant="subtitle2" noWrap>
                                                        {index + 1}. {getWidgetTitle(widget)}
                                                    </Typography>
                                                </Stack>
                                                <IconButton
                                                    size="small"
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        removeWidget(widget.id);
                                                    }}
                                                    sx={{ p: 0.5 }}
                                                >
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </Stack>
                                        </Box>
                                    );
                                })}
                            </Stack>
                        </Stack>

                        <Stack gap={2} flex={1} minWidth={0}>
                            {selectedWidget ? (
                                <>
                                    <Typography variant="h5">
                                        {getWidgetTitle(selectedWidget)}
                                    </Typography>
                                    {renderWidgetEditor(selectedWidget)}
                                    <Box
                                        sx={{
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            borderRadius: 1.5,
                                            bgcolor: 'background.paper',
                                            height: 240,
                                            p: 1.5,
                                            overflow: 'hidden',
                                        }}
                                    >
                                        {renderWidgetPreview(selectedWidget)}
                                    </Box>
                                </>
                            ) : (
                                <Typography color="text.secondary">
                                    {t(
                                        'Modules.Main.Campaigns.Overview.SummaryCustomization.addWidget',
                                    )}
                                </Typography>
                            )}
                        </Stack>
                    </Stack>
                </>
            )}
        </Stack>
    );

    if (inline) {
        return (
            <Box>
                {body}
                <Box sx={{ mt: 3 }}>{actions}</Box>
            </Box>
        );
    }

    return (
        <DialogV2
            open={open}
            onClose={() => setOpen(false)}
            title={t('Modules.Main.Campaigns.Overview.ActionLabels.customizeSummary')}
            maxWidth="xl"
            actions={actions}
            MuiDialogContentProps={{
                sx: {
                    minHeight: '72vh',
                },
            }}
        >
            {body}
        </DialogV2>
    );
};

export default SummaryCustomizationDialog;
